import {
  TextField,
  Button,
  ButtonGroup,
  Typography,
  AppHeader,
  Form,
  useFirstNameValidator,
  usePhoneNumberValidator,
  Stack,
  useEmailAddressValidator,
  useLastNameValidator,
  Container,
  Select,
  MenuItem,
  useSelectValidator,
} from '@ace-clublabs/shared-component-library';
import React, { useContext, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Donor, Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { getNextPage } from '../utils/getNextPage';
import { calculateNewCursorPosition, TextInputChangeEvent, MobileNumberInputType } from '../utils/formatters';
import { GenderListItem, getGenderList, getProgressProps } from '../utils/helpers';
import { UtagType } from '../utils/UtagHelper';
import { AppProgressHeader } from '../components/AppProgressHeader';

export const GiftDonorContactPage: React.FC = () => {
  const { sessionData, membershipInfo, refreshSession, loadingInfo, sendTag, updateMembershipForm } =
    useContext(MembershipAppStateContext);

  const { formData } = sessionData;
  const { giftDetails } = formData;
  const genderList = getGenderList(membershipInfo);
  const genderValidator = useSelectValidator({ validationFeedback: 'immediate' });

  const phoneRef = useRef(null);

  const history = useHistory();
  const [isTagging, setIsTagging] = useState(true);

  const getInitialGiftSenderContactState = (): Donor => {
    return {
      firstName: giftDetails?.donor?.firstName || '',
      lastName: giftDetails?.donor?.lastName || '',
      gender: giftDetails?.donor?.gender || '',
      mobileNumber: giftDetails?.donor?.mobileNumber || '',
      email: giftDetails?.donor?.email || '',
    };
  };

  const [giftSenderContactForm, setGiftSenderContactForm] = useState(getInitialGiftSenderContactState());
  const [cursorPosition, setCursorPosition] = useState(0);

  const firstNameValidator = useFirstNameValidator({
    maxLength: 20,
  });

  const lastNameValidator = useLastNameValidator({
    maxLength: 20,
  });

  const emailValidator = useEmailAddressValidator();

  const phoneNumberValidator = usePhoneNumberValidator();

  const submitForm: () => void = () => {
    history.push(getNextPage(Pages.GiftDonorContactPage, sessionData.flowType));
  };

  const progressProps = getProgressProps(sessionData.flowType!, Pages.GiftDonorContactPage);

  const handleMobileNumberFieldChange = (event: TextInputChangeEvent): void => {
    const updatedCursorPosition = refreshMobileNumberFieldCursorPositionAfterChangeEvent(event);
    setCursorPosition(updatedCursorPosition);
    setGiftSenderContactForm((prev) => ({ ...prev, mobileNumber: event.currentTarget.value }));
  };

  const refreshMobileNumberFieldCursorPositionAfterChangeEvent = (event: TextInputChangeEvent): number => {
    const { inputType } = event.nativeEvent as InputEvent;
    // @ts-ignore
    const inputNode = phoneRef?.current.childNodes[1].childNodes[0];
    const previousCursorPosition = cursorPosition ?? 0;
    const changeEventCursorPosition = inputNode?.selectionStart ?? 0;

    const updatedCursorPosition = calculateNewCursorPosition(
      inputType as MobileNumberInputType,
      previousCursorPosition,
      changeEventCursorPosition
    );

    queueMicrotask(() => inputNode.setSelectionRange(updatedCursorPosition, updatedCursorPosition));

    return updatedCursorPosition;
  };

  const handleMobileNumberFieldSelectionEvent = (event: React.SyntheticEvent<HTMLInputElement, Event>): void => {
    // @ts-ignore
    const inputNode = phoneRef?.current.childNodes[1].childNodes[0];
    const selectionEventCursorPosition = inputNode.selectionStart;
    setCursorPosition(selectionEventCursorPosition ?? 0);
  };

  useEffect(() => {
    const updatedFormData = {
      ...sessionData.formData,
      giftDetails: {
        ...sessionData.formData.giftDetails,
        donor: {
          ...sessionData.formData.giftDetails?.donor,
          ...giftSenderContactForm,
        },
      },
    };
    updateMembershipForm(updatedFormData);
  }, [giftSenderContactForm]);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }
    //!!!!update tag
    sendTag(UtagType.view, 'membership:gift:donor');
    setIsTagging(false);
  }, [membershipInfo]);

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <Container>
      <AppProgressHeader {...progressProps} styleOverrides={{ marginBottom: '1rem' }} />

      <Stack gap={'1rem'}>
        <Typography variant="h5" className="gift-sender-contact-header-title">
          Who is sending this gift?
        </Typography>
        <Typography variant="b1Regular" className="gift-sender-contact-header-text">
          We need information about the person gifting this membership.
        </Typography>
      </Stack>

      <div className="gift-sender-contact-form qm-mask">
        <Form onSubmit={submitForm}>
          <Stack gap={'1rem'}>
            <TextField
              defaultValue={giftSenderContactForm.firstName}
              label="First name"
              for="first-name"
              id="firstName"
              fullWidth
              required
              validator={firstNameValidator}
              onChange={(v) => {
                setGiftSenderContactForm((prev) => ({ ...prev, firstName: v.currentTarget.value }));
              }}
              onClearButtonClick={() => {
                setGiftSenderContactForm((prev) => ({ ...prev, firstName: '' }));
              }}
              sx={{
                '& .MuiFormLabel-asterisk': {
                  display: 'none',
                },
              }}
            />
            <TextField
              defaultValue={giftSenderContactForm.lastName}
              label="Last name"
              for="last-name"
              id="lastName"
              fullWidth
              required
              validator={lastNameValidator}
              onChange={(v) => {
                setGiftSenderContactForm((prev) => ({ ...prev, lastName: v.currentTarget.value }));
              }}
              onClearButtonClick={() => {
                setGiftSenderContactForm((prev) => ({ ...prev, lastName: '' }));
              }}
              sx={{
                '& .MuiFormLabel-asterisk': {
                  display: 'none',
                },
              }}
            />

            <Select
              label="Gender"
              id="genderDropdown"
              displayEmpty
              placeholder="Select one"
              helperText={`Must match state driver’s license or I.D.`}
              fullWidth={true}
              value={genderList.find((g) => {
                return g.genderValue === giftSenderContactForm.gender;
              })}
              getDisplayText={(v: any) => {
                return v.genderName;
              }}
              validator={genderValidator}
              required
              onChange={(v) => {
                const gender = v.target.value as GenderListItem;
                const genderValue = gender.genderValue;
                setGiftSenderContactForm((prev) => ({ ...prev, gender: genderValue }));
              }}
            >
              {genderList.map((gender, i) => (
                <MenuItem key={gender.genderName} value={gender as any}>
                  {gender.genderName}
                </MenuItem>
              ))}
            </Select>

            <TextField
              ref={phoneRef}
              label="Mobile number"
              id="mobileNumber"
              defaultValue={giftSenderContactForm.mobileNumber}
              validator={phoneNumberValidator}
              for="phone-number"
              onChange={handleMobileNumberFieldChange}
              onClearButtonClick={() => {
                setGiftSenderContactForm((prev) => ({ ...prev, mobileNumber: '' }));
              }}
              InputProps={{
                componentsProps: {
                  input: {
                    onSelect: handleMobileNumberFieldSelectionEvent,
                  },
                },
              }}
              fullWidth
              required
              sx={{
                '& .MuiFormLabel-asterisk': {
                  display: 'none',
                },
              }}
            />
            <TextField
              fullWidth
              name="emailAddress"
              aria-label="emailAddress"
              value={giftSenderContactForm.email}
              label="Email address"
              id="emailAddress"
              validator={emailValidator}
              onChange={(v) => setGiftSenderContactForm((prev) => ({ ...prev, email: v.target.value }))}
              onClearButtonClick={() => {
                setGiftSenderContactForm((prev) => ({ ...prev, email: '' }));
              }}
              sx={{
                '& .MuiFormLabel-asterisk': {
                  display: 'none',
                },
              }}
            />
          </Stack>
          <ButtonGroup sx={{ marginTop: '1.5rem' }} className="gift-sender-contact-details-btn">
            <Button id="nextButton" type="submit" className="btn-large" label="Next" />
          </ButtonGroup>
        </Form>
      </div>
    </Container>
  );
};
